
import { ChevronRight, Close } from '@mui/icons-material';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';

import type { EntitySummary, Location } from '@/lib/types';

import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';


export function PartOfRelationshipManager({
  location,
}: {
  location: Location;
}) {
  const {
    novel,
    getSummaries,
    updateEntityItem
  } = useNovelRepository();
  const { toast } = useToast();
  const [selectedParent, setSelectedParent] = useState<{ id: string; name: string } | null>(null);

  // Fetch all locations to select a parent from
  const allLocations = getSummaries<EntitySummary>('locations');

  // Sync the dropdown with the database, but preserve user's unsaved draft selections
  useEffect(() => {
    if (location.parentId) {
      const parentInList = allLocations.find(l => l.id === location.parentId);
      const newParent = parentInList
        ? { id: parentInList.id, name: parentInList.name }
        : { id: location.parentId, name: 'Loading...' };

      setSelectedParent(prev => {
        // If the user hasn't drafted a new selection, keep it in sync with the DB (e.g. if the name changes)
        if (!prev || prev.id === location.parentId) {
          // Bail out if identically deep-equal to avoid unnecessary re-renders
          if (prev?.id === newParent.id && prev?.name === newParent.name) return prev;
          return newParent;
        }
        // User has drafted a new selection, don't overwrite it
        return prev;
      });
    } else {
      setSelectedParent(prev => {
        // Only null it out if the user hasn't drafted a new selection
        if (!prev || prev.id === location.parentId) return null;
        return prev;
      });
    }
  }, [location.parentId, allLocations]);

  // Compute path synchronously on render
  const path: { id: string; name: string }[] = [{ id: location.id, name: location.name }];
  let currentId = location.parentId;
  const visited = new Set<string>([location.id]);

  while (currentId && !visited.has(currentId)) {
    visited.add(currentId);
    const parentSummary = allLocations.find(l => l.id === currentId);

    if (parentSummary) {
      path.unshift({ id: parentSummary.id, name: parentSummary.name });
      currentId = parentSummary.parentId;
    } else {
      break;
    }
  }

  const handleSetParent = async () => {
    if (!novel.id) return;

    await updateEntityItem('locations', location.id, {
      parentId: selectedParent?.id || null,
    });

    toast({
      title: "Hierarchy Updated",
      description: `${location.name} is now part of ${selectedParent?.name}.`
    });
  };

  const handleRemoveParent = async () => {
    if (!novel.id) return;
    await updateEntityItem('locations', location.id, {
      parentId: null,
    });
    toast({
      title: "Hierarchy Updated",
      description: `${location.name} is no longer part of a parent location.`
    });
    setSelectedParent(null);
  };

  const availableLocations = allLocations?.filter(l => l.id !== location.id) || [];

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Location Hierarchy
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Define where this location exists within your world.
      </Typography>

      <Stack spacing={3}>
        {/* Path Display */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          {path.map((loc, index) => (
            <Box key={loc.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {index > 0 && <ChevronRight sx={{ fontSize: 16, color: 'text.secondary' }} />}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: loc.id === location.id ? 600 : 400,
                  color: loc.id === location.id ? 'primary.main' : 'text.secondary'
                }}
              >
                {loc.name}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Parent Selector */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            Is Part Of
          </Typography>
          <Stack direction="row" spacing={1}>
            <Autocomplete
              fullWidth
              options={availableLocations}
              getOptionLabel={(option) => option.name}
              value={selectedParent}
              onChange={(event, newValue) => setSelectedParent(newValue ? { id: newValue.id, name: newValue.name } : null)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select a parent location..."
                  size="small"
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
            {selectedParent && (
              <IconButton size="small" onClick={handleRemoveParent} sx={{ flexShrink: 0 }}>
                <Close fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Box>

        <Button
          variant="contained"
          onClick={handleSetParent}
          disabled={!selectedParent}
          fullWidth
        >
          Set Parent
        </Button>
      </Stack>
    </Paper>
  );
}
